import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useLanguage } from "../../Translations/LenguageContext";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";
import { useParams } from "react-router-dom";
import { DiMsqlServer } from "react-icons/di";
import { BiLogoPostgresql, BiPlus } from "react-icons/bi";

const InstanceModal = ({
    show,
    handleClose,
    azureRegions,
    selectedRegion,
    setRegion,
    continentFlags,
    regionDisplayNames,
    fetchInstances
}) => {
    const { t } = useLanguage();
    const user_id = sessionStorage.getItem("user_id");
    const { project_id } = useParams();

    const [instanceName, setInstanceName] = useState("");
    const [instanceDescription, setInstanceDescription] = useState("");
    const [accessName, setAccessName] = useState("");
    const [accessPassword, setAccessPassword] = useState("");
    const [firewallRules, setFirewallRules] = useState([{ start_ip: "", end_ip: "" }]); // minimum one row
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({ message: "", display: false, fields: {} });
    const [sqlType, setSqlType] = useState("postgresql");

    useEffect(() => {
        if (show) {
            setInstanceName("");
            setInstanceDescription("");
            setAccessName("");
            setAccessPassword("");
            setFirewallRules([{ start_ip: "", end_ip: "" }]);
            setLoading(false);
            setSqlType("postgresql");
            setRegion("italynorth");
            setError({ message: "", display: false, fields: {} });
        }
    }, [show]);

    const CloseModal = () => {
        handleClose();
        setError({ message: "", display: false, fields: {} });
    };

    const handleFirewallChange = (index, field, value) => {
        const newRules = [...firewallRules];
        newRules[index][field] = value;
        setFirewallRules(newRules);
    };

    const addFirewallRule = () => {
        setFirewallRules([...firewallRules, { start_ip: "", end_ip: "" }]);
    };

    const removeFirewallRule = (index) => {
        if (firewallRules.length > 1) {
            const newRules = firewallRules.filter((_, i) => i !== index);
            setFirewallRules(newRules);
        }
    };

    const saveinstance = async () => {
        const newError = { message: "", display: false, fields: {} };

        if (!instanceName.trim()) newError.fields.instanceName = true;
        if (!instanceDescription.trim()) newError.fields.instanceDescription = true;
        if (!accessName.trim()) newError.fields.accessName = true;
        if (!accessPassword.trim()) newError.fields.accessPassword = true;

        // Validate firewall rules
        function isValidIp(ip) {
            const regex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
            return regex.test(ip.trim());
        }

        firewallRules.forEach((rule, i) => {
            if (!isValidIp(rule.start_ip)) newError.fields[`startIp${i}`] = true;
            if (!isValidIp(rule.end_ip)) newError.fields[`endIp${i}`] = true;
        });

        const passwordError = validatePassword(accessPassword, accessName);
        if (passwordError) {
            newError.fields.accessPassword = true;
            newError.message = passwordError;
            newError.display = true;
            setError(newError);
            return;
        }

        if (Object.keys(newError.fields).length > 0) {
            newError.message = newError.message || "Please fill all required fields correctly.";
            newError.display = true;
            setError(newError);
            return;
        }

        setLoading(true);
        setError({ message: "", display: false, fields: {} });

        const data = {
            user_id,
            project_id,
            instance_name: instanceName,
            instance_description: instanceDescription,
            access_name: accessName,
            access_password: accessPassword,
            selected_region: selectedRegion,
            sql_type: sqlType,
            firewall_rules: firewallRules, 
        };


        try {
            const response = await fetch("https://backend.erioon.com/create_instance", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            setLoading(false);

            if (response.ok) {
                await response.json();
                fetchInstances();
                handleClose();
            } else {
                const errorText = await response.text();
                setError({ message: `Error saving instance: ${errorText}`, display: true, fields: {} });
            }
        } catch (err) {
            setLoading(false);
            setError({ message: "Network error: could not save instance", display: true, fields: {} });
        }
    };

    const validatePassword = (password, username) => {
        if (password.length < 8 || password.length > 128) return "Password must be between 8 and 128 characters long";
        if (/\s/.test(password)) return "Password cannot contain spaces";
        if (username && password.toLowerCase().includes(username.toLowerCase())) return "Password cannot contain the username";

        let categories = 0;
        if (/[a-z]/.test(password)) categories++;
        if (/[A-Z]/.test(password)) categories++;
        if (/\d/.test(password)) categories++;
        if (/[^a-zA-Z0-9]/.test(password)) categories++;

        if (categories < 3) return "Password must contain at least 3 of the following: uppercase, lowercase, number, special character";
        return null;
    };

    const inputClass = (field) => error.fields[field] ? "api_name_input error_input" : "api_name_input";

    const AllowFromAnywhere = () =>{
        setFirewallRules([{ start_ip: "0.0.0.0", end_ip: "255.255.255.255" }]);
    }

    return (
        <Modal show={show} onHide={CloseModal} className="modal_instance">
            <div className="share_main_cont_modal">
                <p className="share_label">Create a new PostgreSQL instance <BiLogoPostgresql/></p>
                <p className="share_label_desc">
                    The instance provides the compute, storage, and networking resources needed for your databases to run. 
                </p>

                <p className="db_title">Instance information</p>
                <div className="modal_inputs_cont">
                    <input
                        className={inputClass("instanceName")}
                        placeholder="Instance name"
                        value={instanceName}
                        onChange={(e) => setInstanceName(e.target.value)}
                    />

                    <textarea
                        className={`api_name_description ${inputClass("instanceDescription")}`}
                        placeholder="A brief description (ex. Instance for staging data)"
                        value={instanceDescription}
                        onChange={(e) => setInstanceDescription(e.target.value)}
                    />

                    <p className="db_title">Instance credentials</p>
                    <input
                        className={inputClass("accessName")}
                        placeholder="Access name"
                        value={accessName}
                        onChange={(e) => setAccessName(e.target.value)}
                        autoComplete="new-usernamename"
                    />

                    <input
                        type="password"
                        className={inputClass("accessPassword")}
                        placeholder="Password"
                        value={accessPassword}
                        onChange={(e) => setAccessPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    
                    <p className="db_title">Firewall settings <button className="add_column_btn" onClick={addFirewallRule}>+ Add IP</button> <button className="add_column_btn" onClick={AllowFromAnywhere}><BiPlus/> Allow from anywhere</button></p>
                    {firewallRules.map((rule, i) => (
                        <div key={i} className="firewall_settings_row">
                            <input
                                className={inputClass(`startIp${i}`)}
                                placeholder="Start IP"
                                value={rule.start_ip}
                                onChange={(e) => handleFirewallChange(i, "start_ip", e.target.value)}
                            />
                            <input
                                className={inputClass(`endIp${i}`)}
                                placeholder="End IP"
                                value={rule.end_ip}
                                onChange={(e) => handleFirewallChange(i, "end_ip", e.target.value)}
                            />
                            <button className="remove_row_btn" onClick={() => removeFirewallRule(i)} disabled={firewallRules.length === 1}>x</button>
                        </div>
                    ))}
                </div>

                {/* <p className="db_title">Choose a database type</p>
                <div className="vendor_cont">
                    <div className={`vendor_item ${sqlType === "postgresql" ? "vendor_item_selected" : ""}`} onClick={() => setSqlType("postgresql")}>
                        <p className="vendor_logo"><BiLogoPostgresql /></p>
                        <p className="full_vendor_name">PostgreSQL</p>
                    </div>
                    <div className={`vendor_item ${sqlType === "sqlserver" ? "vendor_item_selected" : ""}`} onClick={() => setSqlType("sqlserver")}>
                        <p className="vendor_logo"><DiMsqlServer /></p>
                        <p className="full_vendor_name">SQL Server</p>
                    </div>
                </div> */}

                <p className="db_title">Choose a location</p>
                <div className="region_container">
                    <div className="region_block">
                        {["North America", "South America", "Australia"].map((continent) =>
                            azureRegions[continent] ? (
                                <div key={continent} className="continent_region">
                                    <p className="continent_title_region">
                                        {continentFlags[continent]} {continent}
                                    </p>
                                    <div className="reg_cont_main">
                                        {azureRegions[continent].map((region) => (
                                            <div key={region} value={region} className={selectedRegion === region ? "single_region_selected" : "single_region"} onClick={() => setRegion(region)}>
                                                {regionDisplayNames[region] || region}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>

                    <div className="region_block">
                        {["Europe", "Africa"].map((continent) =>
                            azureRegions[continent] ? (
                                <div key={continent} className="continent_region">
                                    <p className="continent_title_region">
                                        {continentFlags[continent]} {continent}
                                    </p>
                                    <div className="reg_cont_main">
                                        {azureRegions[continent].map((region) => (
                                            <div key={region} value={region} className={selectedRegion === region ? "single_region_selected" : "single_region"} onClick={() => setRegion(region)}>
                                                {regionDisplayNames[region] || region}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>

                    <div className="region_block">
                        {["Asia", "Middle East"].map((continent) =>
                            azureRegions[continent] ? (
                                <div key={continent} className="continent_region">
                                    <p className="continent_title_region">
                                        {continentFlags[continent]} {continent}
                                    </p>
                                    <div className="reg_cont_main">
                                        {azureRegions[continent].map((region) => (
                                            <div key={region} value={region} className={selectedRegion === region ? "single_region_selected" : "single_region"} onClick={() => setRegion(region)}>
                                                {regionDisplayNames[region] || region}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            </div>

            <div className="modal_footer">
                <ErrorMessage err={error} />
                <div className="footer_btn_cont">
                    <button
                        className="save_btn"
                        onClick={saveinstance}
                        disabled={!instanceName.trim()}
                    >
                        {loading ? <Spinner loading={loading} /> : t.create + " instance"}
                    </button>
                    <button className="cancel_btn" onClick={CloseModal}>
                        {t.cancel}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default InstanceModal;
